import React, { useEffect, useMemo, useState } from 'react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (token: string) => void;
}

type Mode = 'login' | 'register' | 'forgot' | 'resend' | 'reset' | 'verify';

const API_BASE = '/api/user/auth';

// Robuster POST-Helper
async function postJson(url: string, body: any) {
    const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });

    const ct = res.headers.get('content-type') || '';
    const parse = async () => (ct.includes('application/json') ? res.json() : { message: await res.text() });
    const data = await parse();

    if (!res.ok) {
        const msg =
            (data && (data.message || data.error)) ||
            `Fehler (${res.status})${ct.includes('text/html') ? ': Unerwartete HTML-Antwort' : ''}`;
        throw new Error(msg);
    }
    return data;
}

function useQueryParams() {
    return useMemo(() => {
        try {
            const usp = new URLSearchParams(window.location.search);
            const token = usp.get('token') || '';
            const email = usp.get('email') || '';
            const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
            return { token, email, path };
        } catch {
            return { token: '', email: '', path: '' };
        }
    }, []);
}

export default function UserAuthModal({ isOpen, onClose, onSuccess }: Props) {
    const [mode, setMode] = useState<Mode>('login');

    // shared fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // register
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    // reset
    const [token, setToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newPassword2, setNewPassword2] = useState('');

    // ui
    const [error, setError] = useState('');
    const [msg, setMsg] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const qp = useQueryParams();

    // Optional: Automodus anhand URL
    useEffect(() => {
        if (!isOpen) return;
        if (qp.token && qp.email) {
            if (qp.path.includes('reset-password')) {
                setMode('reset');
                setEmail(qp.email);
                setToken(qp.token);
            } else if (qp.path.includes('verify-email')) {
                setMode('verify');
                setEmail(qp.email);
                setToken(qp.token);
            }
        }
    }, [isOpen, qp]);

    if (!isOpen) return null;

    const switchTo = (m: Mode) => {
        setError('');
        setMsg(null);
        setMode(m);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setMsg(null);
        setLoading(true);

        try {
            if (mode === 'forgot') {
                const data = await postJson(`${API_BASE}/forgot-password`, { email });
                setMsg(data.message || 'Wenn ein Konto existiert, wurde eine E-Mail gesendet.');
                return;
            }

            if (mode === 'resend') {
                const data = await postJson(`${API_BASE}/resend-verification`, { email });
                setMsg(data.message || 'Falls ein Account existiert, wurde eine E-Mail gesendet.');
                return;
            }

            if (mode === 'verify') {
                const data = await postJson(`${API_BASE}/verify-email`, { email, token });
                setMsg(data.message || 'E-Mail erfolgreich bestätigt.');
                // optional: automatisch zum Login
                // setTimeout(() => setMode('login'), 1500);
                return;
            }

            if (mode === 'reset') {
                if (!newPassword || newPassword.length < 8) {
                    throw new Error('Das neue Passwort muss mindestens 8 Zeichen haben.');
                }
                if (newPassword !== newPassword2) {
                    throw new Error('Die Passwörter stimmen nicht überein.');
                }
                const data = await postJson(`${API_BASE}/reset-password`, { email, token, newPassword });
                setMsg(data.message || 'Passwort erfolgreich geändert. Bitte einloggen.');
                // optional: automatisch zum Login
                // setTimeout(() => setMode('login'), 1500);
                return;
            }

            if (mode === 'register') {
                const data = await postJson(`${API_BASE}/register`, { firstName, lastName, email, password });
                setMsg(data.message || 'Registrierung erfolgreich – bitte E-Mail zur Bestätigung prüfen.');
                // optional: setTimeout(() => setMode('login'), 1500);
                return;
            }

            // login
            const data = await postJson(`${API_BASE}/login`, { email, password });
            if (data.token) {
                localStorage.setItem('userToken', data.token);
                onSuccess(data.token);
                onClose();
            } else {
                setMsg('Login erfolgreich.');
            }
        } catch (err: any) {
            const message = String(err?.message || 'Unbekannter Fehler');

            // Spezialfall: unverifiziert → Shortcut anbieten
            if (mode === 'login' && /bestätige.*E-?Mail/i.test(message)) {
                setError(
                    'Deine E-Mail ist noch nicht bestätigt. Du kannst dir einen neuen Bestätigungslink senden lassen.'
                );
            } else {
                setError(message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
            <div className="relative bg-white p-6 rounded shadow-lg w-full max-w-md text-black">
                <h2 className="text-xl font-bold mb-4">
                    {mode === 'login' && 'Login'}
                    {mode === 'register' && 'Konto erstellen'}
                    {mode === 'forgot' && 'Passwort zurücksetzen – Link anfordern'}
                    {mode === 'resend' && 'Bestätigungslink erneut senden'}
                    {mode === 'reset' && 'Neues Passwort setzen'}
                    {mode === 'verify' && 'E-Mail verifizieren'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-3">
                    {/* REGISTER Felder */}
                    {mode === 'register' && (
                        <>
                            <input
                                placeholder="Vorname"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                                autoComplete="given-name"
                                required
                            />
                            <input
                                placeholder="Nachname"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                                autoComplete="family-name"
                                required
                            />
                        </>
                    )}

                    {/* E-Mail in allen Modi außer ggf. Login-only? -> Wir brauchen sie überall */}
                    <input
                        type="email"
                        placeholder="E-Mail"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className="w-full border px-3 py-2 rounded"
                        autoComplete={mode === 'register' ? 'email' : 'username'}
                        required
                    />

                    {/* Passwortfelder */}
                    {(mode === 'login' || mode === 'register') && (
                        <input
                            type="password"
                            placeholder="Passwort"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className="w-full border px-3 py-2 rounded"
                            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                            required
                        />
                    )}

                    {/* Verify-Modus braucht nur Token + Email */}
                    {mode === 'verify' && (
                        <input
                            type="text"
                            placeholder="Token aus dem Link"
                            value={token}
                            onChange={e => setToken(e.target.value)}
                            className="w-full border px-3 py-2 rounded"
                            required
                        />
                    )}

                    {/* Reset-Modus: Token + neues Passwort zweimal */}
                    {mode === 'reset' && (
                        <>
                            <input
                                type="text"
                                placeholder="Reset-Token"
                                value={token}
                                onChange={e => setToken(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                                required
                            />
                            <input
                                type="password"
                                placeholder="Neues Passwort (min. 8 Zeichen)"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                                autoComplete="new-password"
                                required
                                minLength={8}
                            />
                            <input
                                type="password"
                                placeholder="Neues Passwort wiederholen"
                                value={newPassword2}
                                onChange={e => setNewPassword2(e.target.value)}
                                className="w-full border px-3 py-2 rounded"
                                autoComplete="new-password"
                                required
                                minLength={8}
                            />
                        </>
                    )}

                    {error && <div className="text-red-600 text-sm">{error}</div>}
                    {msg && <div className="text-green-700 text-sm">{msg}</div>}

                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 w-full rounded disabled:opacity-60"
                        disabled={loading}
                    >
                        {loading
                            ? 'Bitte warten...'
                            : mode === 'login'
                                ? 'Einloggen'
                                : mode === 'register'
                                    ? 'Registrieren'
                                    : mode === 'forgot'
                                        ? 'Reset-Link senden'
                                        : mode === 'resend'
                                            ? 'Bestätigungslink senden'
                                            : mode === 'verify'
                                                ? 'E-Mail jetzt bestätigen'
                                                : 'Passwort setzen'}
                    </button>
                </form>

                {/* Links unten */}
                <div className="mt-4 text-sm text-center space-y-2">
                    {mode === 'login' && (
                        <>
                            <button onClick={() => switchTo('forgot')} className="text-blue-600 underline block">
                                Passwort vergessen?
                            </button>

                            {/* Shortcut nach 403 */}
                            {error && /bestätige.*E-?Mail/i.test(error) && (
                                <button onClick={() => switchTo('resend')} className="text-blue-600 underline block">
                                    Bestätigungslink erneut senden
                                </button>
                            )}

                            <div>
                                Noch kein Konto?{' '}
                                <button onClick={() => switchTo('register')} className="text-blue-600 underline">
                                    Jetzt registrieren
                                </button>
                            </div>
                        </>
                    )}

                    {mode === 'register' && (
                        <div>
                            Bereits ein Konto?{' '}
                            <button onClick={() => switchTo('login')} className="text-blue-600 underline">
                                Einloggen
                            </button>
                        </div>
                    )}

                    {(mode === 'forgot' || mode === 'resend' || mode === 'reset' || mode === 'verify') && (
                        <div>
                            Zurück zum{' '}
                            <button onClick={() => switchTo('login')} className="text-blue-600 underline">
                                Login
                            </button>
                        </div>
                    )}
                </div>

                <button
                    onClick={onClose}
                    className="absolute top-2 right-4 text-gray-500 text-xl"
                    aria-label="Modal schließen"
                >
                    &times;
                </button>
            </div>
        </div>
    );
}
